/**
 * Centralised logger that no-ops in production builds when
 * REACT_APP_DISABLE_LOGS=1 is set. Use everywhere instead of `console.*` so
 * we never ship verbose logs to end-users' DevTools.
 *
 * Errors are still surfaced (so Sentry/Datadog can capture them) but as
 * single-line messages without the request bodies / tokens that may leak
 * sensitive data.
 */

const ENABLED = process.env.NODE_ENV !== 'production' || process.env.REACT_APP_DISABLE_LOGS !== '1';

type LogFn = (...args: unknown[]) => void;

const noop: LogFn = () => {};

export const logger: { log: LogFn; info: LogFn; warn: LogFn; error: LogFn; debug: LogFn } = ENABLED
  ? {
      // eslint-disable-next-line no-console
      log: (...args) => console.log(...args),
      // eslint-disable-next-line no-console
      info: (...args) => console.info(...args),
      // eslint-disable-next-line no-console
      warn: (...args) => console.warn(...args),
      // eslint-disable-next-line no-console
      error: (...args) => console.error(...args),
      // eslint-disable-next-line no-console
      debug: (...args) => console.debug(...args),
    }
  : { log: noop, info: noop, warn: noop, error: noop, debug: noop };

/**
 * Aggressive scrub for production: monkey-patch the global console so any
 * third-party library's stray console.log is silenced too. Only call once
 * during app bootstrap.
 */
export function silenceConsoleInProduction(): void {
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV !== 'production') return;
  if (process.env.REACT_APP_DISABLE_LOGS !== '1') return;
  const c = window.console;
  if (!c) return;
  c.log = noop;
  c.info = noop;
  c.debug = noop;
  c.warn = noop;
  // Keep c.error so production crashes still surface in error-tracking tools.
}
